import React from 'react';

import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {VALUE_VALIDATORS} from 'insights/parameters/data/ValueValidators';
import ParameterTypeHelpButton from 'insights/parameters/ParameterTypeHelpButton';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import nameToSqlIdentifier from 'warehouse/nameToSqlIdentifier';

import {AnalyticsParameterConfigGraphQL as ParameterConfig} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import getParameterType from './data/getParameterType';
import sortParameterConfigs from './data/sortParameterConfigs';
import EditParametersModalRow from './EditParametersModalRow';
import ParameterInfoButton from './ParameterInfoButton';
import useAnalyticsParametersState from './useAnalyticsParametersState';

function getSimilarConfigNames(configs: Array<ParameterConfig>): Array<string> {
  const identifierToConfigNames: {[key: string]: Array<string>} = {};

  for (const config of configs) {
    const sqlIdentifier = nameToSqlIdentifier(config.systemName, false);

    if (identifierToConfigNames[sqlIdentifier] == null) {
      identifierToConfigNames[sqlIdentifier] = [];
    }

    identifierToConfigNames[sqlIdentifier].push(config.systemName);
  }

  return Object.values(identifierToConfigNames)
    .filter((names) => names.length > 1)
    .flat();
}

export interface EditParametersModalProps {
  parameterConfigs: Array<ParameterConfig>;
  parameterValues: Array<AnalyticsParameterValue>;
  onRequestClose: () => void;
  onRequestSubmit: (result: {
    parameterConfigs: Array<ParameterConfig>;
    parameterValues: Array<AnalyticsParameterValue>;
  }) => void;
  writable: boolean;
}

export default function EditAnalyticsParametersModal(props: EditParametersModalProps): JSX.Element {
  const {configs, valueStates, clearAllValues, updateConfig, updateValue} = useAnalyticsParametersState(
    props.parameterConfigs,
    props.parameterValues
  );
  const areCurrentValuesSaveable = Object.values(valueStates).every(({isValid}) => isValid);
  const orderedConfigIds = sortParameterConfigs(props.parameterConfigs).map((config) => config.id);
  const excessivelySimilarNames = getSimilarConfigNames(Object.values(configs));

  return (
    <Modal
      large={true}
      buttons={[
        {
          asLink: true,
          style: ButtonStyle.LINK,
          text: 'Clear all parameter values',
          onClick: clearAllValues,
          pullLeft: true,
        },
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Save',
          disabled:
            !areCurrentValuesSaveable ||
            excessivelySimilarNames.length > 1 ||
            !Object.values(configs).every((config) => config.systemName.length > 0),
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit({
          parameterConfigs: Object.values(configs),
          parameterValues: Object.values(valueStates)
            .filter(({value}) =>
              VALUE_VALIDATORS[getParameterType(configs[value.config.id])](value.valueJson)
            )
            .map(({value}) => value),
        });
      }}
      title="Edit parameters"
    >
      <FormGroup>
        <Row>
          <Col sm={3}>
            <FieldLabel label="Name" />
          </Col>
          <Col sm={3}>
            <FieldLabel label="Type" />
            <ParameterTypeHelpButton className="u-mini-spacing-left" />
          </Col>
          <Col sm={4}>
            <FieldLabel label="Value" />
          </Col>
          <Col sm={2}>
            <FieldLabel label="Multi-select" />
            <ParameterInfoButton largeModal={true} />
          </Col>
        </Row>
        {orderedConfigIds.map((configId) => (
          <EditParametersModalRow
            config={configs[configId]}
            value={valueStates[configId]?.value}
            valueJsonIsValid={valueStates[configId]?.isValid ?? true}
            key={configId}
            onUpdateConfig={updateConfig}
            onUpdateValue={updateValue}
            writable={props.writable}
          />
        ))}
      </FormGroup>
      {excessivelySimilarNames.length > 1 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Config names are too similar: ${excessivelySimilarNames.join(', ')}`}
        />
      )}
    </Modal>
  );
}
